import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Download,
  User,
  LayoutGrid,
  CalendarDays,
  TrendingUp,
  DollarSign,
  BarChart3,
  Filter,
  ChevronLeft,
  ChevronRight,
  Zap,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Button } from '../../components/shared';
import { DatePicker } from '../../components/client';
import DataTable from '../../components/shared/DataTable';
import {
  useVendorBookings,
  useBookingStatistics,
} from '../../services/vendorBookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const VendorAdvancedBookings = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'calendar'
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    service_id: '',
    client_search: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());

  const cleanFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(
          ([_, v]) => v !== '' && v != null && v !== undefined
        )
      ),
    [filters]
  );

  const { data: bookingsResponse, isLoading, error } = useVendorBookings(cleanFilters);
  const { data: statsResponse } = useBookingStatistics();

  const stats = bookingsResponse?.statistics || statsResponse?.data || {};
  const paginator = bookingsResponse?.data?.data || {};
  const bookings = useMemo(
    () => paginator?.data || bookingsResponse?.data?.data || [],
    [paginator?.data, bookingsResponse?.data?.data]
  );

  // Chart data: monthly bookings & revenue trend
  const monthlyBreakdown = useMemo(() => stats.monthly_breakdown || {}, [stats.monthly_breakdown]);
  const trendData = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const m = monthlyBreakdown[month];
      return {
        name: MONTH_NAMES[i],
        bookings: m?.count ?? 0,
        revenue: parseFloat(m?.revenue || 0),
      };
    });
  }, [monthlyBreakdown]);

  // Bookings by service (computed from current page + all if needed)
  const bookingsByServiceData = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      const name = b.vendor_service?.service_name || 'Unknown';
      if (!map[name]) map[name] = { name, count: 0, revenue: 0 };
      map[name].count += 1;
      const amt = parseFloat(b.vendor_payout_amount || b.subtotal_amount || 0);
      map[name].revenue += amt;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 7);
  }, [bookings]);

  const handleStatusTab = (status) => {
    setFilters((prev) => ({ ...prev, status }));
  };

  const handleDateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      service_id: '',
      client_search: '',
    });
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    const t = searchTerm.toLowerCase();
    return data.filter(
      (b) =>
        b.booking_reference?.toLowerCase().includes(t) ||
        b.client?.name?.toLowerCase().includes(t) ||
        b.vendor_service?.service_name?.toLowerCase().includes(t) ||
        b.event_location?.toLowerCase().includes(t)
    );
  };

  const handleExportCSV = () => {
    const headers = [
      'Booking #',
      'Client',
      'Service',
      'Event Date',
      'Time',
      'Amount',
      'Status',
    ];
    const rows = bookings.map((b) => [
      b.booking_reference,
      b.client?.name || '',
      b.vendor_service?.service_name || '',
      b.event_date,
      b.start_time || '',
      b.vendor_payout_amount ?? b.subtotal_amount ?? 0,
      b.status,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const getStatusConfig = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      confirmed: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle },
    };
    return config[status] || config.pending;
  };

  const columns = [
    {
      key: 'booking_reference',
      label: 'Booking #',
      sortable: true,
      width: 'w-48',
      render: (b) => (
        <span className="font-mono text-sm font-medium text-primary-600">
          #{b.booking_reference}
        </span>
      ),
    },
    {
      key: 'client',
      label: 'Client',
      sortable: false,
      width: 'w-48',
      render: (b) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-primary-600" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {b.client?.name || 'Unknown'}
            </div>
            <div className="text-sm text-gray-500 truncate">{b.client?.phone}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'service',
      label: 'Service',
      sortable: false,
      width: 'w-48',
      render: (b) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">
            {b.vendor_service?.service_name}
          </div>
          <div className="text-sm text-gray-500 truncate">
            {b.vendor_service?.service_category?.category_name}
          </div>
        </div>
      ),
    },
    {
      key: 'event_date',
      label: 'Event Date',
      sortable: true,
      width: 'w-40',
      render: (b) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {formatDate(b.event_date, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {b.start_time ? formatTime(b.start_time) : 'TBD'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      width: 'w-32',
      render: (b) => {
        const amt = parseFloat(b.vendor_payout_amount || b.subtotal_amount || 0);
        return (
          <div className="font-medium text-gray-900 text-right">
            ${isNaN(amt) ? '0.00' : amt.toFixed(2)}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      render: (b) => {
        const cfg = getStatusConfig(b.status);
        const Icon = cfg.icon;
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color} whitespace-nowrap`}
          >
            <Icon className="w-3 h-3 mr-1 flex-shrink-0" />
            {b.status?.replace('_', ' ')}
          </span>
        );
      },
    },
  ];

  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (b) => navigate(`/vendor/bookings/${b.id}`),
    },
  ];

  // Calendar view logic
  const currentMonth = calendarDate.getMonth();
  const currentYear = calendarDate.getFullYear();
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekday = firstDay.getDay();

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentYear, currentMonth, d);
      const dayBookings = bookings.filter((b) => {
        const bd = new Date(b.event_date);
        return bd.toDateString() === date.toDateString();
      });
      days.push({ day: d, date, bookings: dayBookings });
    }
    return days;
  }, [currentMonth, currentYear, bookings, daysInMonth, startWeekday]);

  const totalRevenue = parseFloat(stats.total_revenue || 0);
  const totalBookings = stats.total_bookings || 0;
  const pendingCount = stats.pending_bookings || 0;
  const completedCount = stats.completed_bookings || 0;
  const conversionRate =
    totalBookings > 0 ? ((completedCount / totalBookings) * 100).toFixed(1) : '0';

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">
              Error Loading Advanced Bookings
            </h3>
            <p className="text-red-600">Failed to load data. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-8 h-8 text-primary-500" />
              <h1 className="text-3xl font-bold text-gray-900">Advanced Bookings</h1>
            </div>
            <p className="text-gray-600">
              KPIs, trends, analytics & full control over your bookings
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
            <Button
              variant="outline"
              icon={Filter}
              onClick={() => setShowFilters((s) => !s)}
            >
              {showFilters ? 'Hide Filters' : 'Filters'}
            </Button>
            <Button variant="outline" icon={Download} onClick={handleExportCSV}>
              Export CSV
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalBookings}</p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ${isNaN(totalRevenue) ? '0.00' : totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{pendingCount}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completion Rate</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{conversionRate}%</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Bookings & Revenue Trend ({new Date().getFullYear()})
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    formatter={(value, name) =>
                      name === 'revenue' ? [`$${Number(value).toFixed(2)}`, 'Revenue'] : [value, 'Bookings']
                    }
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="bookings"
                    stroke="#2563EB"
                    fill="url(#colorBookings)"
                    name="Bookings"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10B981"
                    strokeOpacity={0.8}
                    fill="transparent"
                    name="Revenue"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Bookings by Service
            </h3>
            <div className="h-64">
              {bookingsByServiceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={bookingsByServiceData}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={75} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => [v, 'Count']} />
                    <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  No service data in current view
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-t-xl shadow-sm border border-gray-200 border-b-0 px-4 pt-4">
          <div className="flex flex-wrap gap-1">
            {STATUS_TABS.map((tab) => {
              const count =
                tab.key === ''
                  ? totalBookings
                  : stats[`${tab.key}_bookings`] ?? 0;
              const isActive = filters.status === tab.key;
              return (
                <button
                  key={tab.key || 'all'}
                  onClick={() => handleStatusTab(tab.key)}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-500 -mb-px'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                  <span className="ml-1.5 text-gray-400">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* View Toggle & Filters */}
        <div className="bg-white border border-gray-200 border-t-0 px-4 py-3 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg ${viewMode === 'table' ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Table view"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-lg ${viewMode === 'calendar' ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Calendar view"
            >
              <CalendarDays className="w-5 h-5" />
            </button>
          </div>
          {showFilters && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                <DatePicker
                  value={filters.start_date}
                  onChange={(v) => handleDateFilter('start_date', v)}
                  placeholder="Start"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                <DatePicker
                  value={filters.end_date}
                  onChange={(v) => handleDateFilter('end_date', v)}
                  placeholder="End"
                  className="text-sm"
                />
              </div>
              <input
                type="text"
                placeholder="Client search..."
                value={filters.client_search}
                onChange={(e) => setFilters((p) => ({ ...p, client_search: e.target.value }))}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm w-40"
              />
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
          {viewMode === 'table' ? (
            <DataTable
              data={bookings}
              columns={columns}
              actions={actions}
              loading={isLoading}
              searchable
              sortable
              selectable
              onSearch={handleSearch}
              searchPlaceholder="Search by reference, client, service, location..."
              emptyMessage="No bookings match your filters."
              pageSize={15}
              showPagination
            />
          ) : (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][currentMonth]} {currentYear}
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ChevronLeft}
                    onClick={() =>
                      setCalendarDate(
                        new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1)
                      )
                    }
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCalendarDate(new Date())}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ChevronRight}
                    iconPosition="right"
                    onClick={() =>
                      setCalendarDate(
                        new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1)
                      )
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((cell, i) => (
                  <div
                    key={i}
                    className={`min-h-[80px] p-2 rounded-lg border ${
                      cell ? 'bg-white border-gray-200' : 'bg-gray-50 border-transparent'
                    }`}
                  >
                    {cell && (
                      <>
                        <div className="text-sm font-medium text-gray-700 mb-1">{cell.day}</div>
                        {cell.bookings.map((b) => (
                          <button
                            key={b.id}
                            onClick={() => navigate(`/vendor/bookings/${b.id}`)}
                            className="block w-full text-left text-xs px-2 py-1 rounded bg-primary-50 text-primary-700 hover:bg-primary-100 truncate mb-1"
                          >
                            {b.client?.name || 'Client'} • {b.vendor_service?.service_name || 'Service'}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorAdvancedBookings;
